import type { FC } from 'react';
import { BOOK_A_CALL_URL } from '../copy';
import { trackCTAClick } from '@/lib/analytics';

// Two variants for the same CTA, sharing one component so styling and
// analytics stay in lockstep:
//   - `floating`: fixed bottom-right pill on desktop / full-width sticky
//                 bar on mobile. Used on screen so the CTA stays visible
//                 as the reader scrolls.
//   - `inline`:   in document flow, left-aligned, sits at the end of the
//                 value prop. Used in the printed PDF where there is no
//                 scrolling and the CTA needs to land on the page.
export type BookACallVariant = 'floating' | 'inline';

const SHARED =
  'inline-flex items-center justify-center font-semibold text-white no-underline ' +
  'bg-unimo-onepager-cta hover:bg-unimo-onepager-cta-hover ' +
  'transition-colors duration-150 ease-out ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-unimo-onepager-cta/30';

// Floating variant needs to stay legible over the illustration band at
// the bottom of the page, where the CTA can land on top of the coral /
// teal / purple shapes. A paper-coloured ring + soft drop shadow lifts
// it off any background.
const FLOATING =
  // Mobile: full-width sticky bar — shadowed top edge gives
  // separation from whatever's behind it; no ring needed (full bleed).
  'fixed z-20 inset-x-0 bottom-0 h-14 rounded-none text-[17px] ' +
  'shadow-[0_-6px_18px_rgba(28,28,28,0.18)] ' +
  // Desktop: floating pill bottom-right with page-coloured ring +
  // ambient shadow. ring-offset gives the visible gap between pill and
  // ring so the dark button reads cleanly on any backdrop.
  'md:inset-x-auto md:right-6 md:bottom-6 md:h-auto md:min-h-[52px] ' +
  'md:px-6 md:py-3.5 md:rounded-lg md:text-[16px] ' +
  'md:ring-2 md:ring-unimo-onepager-bg md:ring-offset-0 ' +
  'md:shadow-[0_10px_24px_rgba(28,28,28,0.22)]';

const INLINE = 'self-start min-h-[52px] px-6 py-3.5 rounded-lg text-[16px]';

export const BookACallButton: FC<{
  variant: BookACallVariant;
  /** Industry slug for analytics — distinguishes which one-pager was the source. */
  analyticsSlug: string;
}> = ({ variant, analyticsSlug }) => (
  <a
    href={BOOK_A_CALL_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackCTAClick('book_a_call', `${analyticsSlug}_one_pager:${variant}`)}
    className={`${SHARED} ${variant === 'floating' ? FLOATING : INLINE}`}
    style={{ letterSpacing: '0.005em' }}
  >
    Book a call
  </a>
);
